import { FilterByNumberAppliedFilterView } from "@/domain/view/components/datatable/datatable-filters/filter-by-number-applied-filter-view";
import { FilterByNumberConfigurationView } from "@/domain/view/components/datatable/datatable-filters/filter-by-number-configuration-view";
import { FilterByNumberDisplayView } from "@/domain/view/components/datatable/datatable-filters/filter-by-number-display-view";
import { filterByNumberWithUnknownPayload } from "@/domain/view/components/datatable/datatable-filters/functions/filter-by-number";
import { DatatableCellNumber } from "@/domain/view/components/datatable/datatable-template-cells/datatable-cell-number";
import { DatatableColumnHeaderCell } from "@/domain/view/components/datatable/datatable-template-cells/datatable-column-header-cell";
import { DatatableColumn, isRawRow } from "@/domain/view/components/datatable/datatable-types";
import { Workspace } from "@/domain/workspace/models/workspace-model";

import { IndicatorListItem } from "../../../models/indicator-model";

export const COLUMN_ID_INDICATOR_LATEST_VALUE = "indicator_latest_value";
export const COLUMN_ORDER_VALUE_INDICATOR_LATEST_VALUE = 107;
export const COLUMN_ID_INDICATOR_LATEST_VALUE_DISPLAY_VALUE = "Neuster Wert";

interface IndicatorLatestValueColumnProps {
  workspace: Workspace;
  columnId?: string;
  orderValue?: number;
  displayValue?: string;
}

export const IndicatorLatestValueColumn = ({
  columnId = COLUMN_ID_INDICATOR_LATEST_VALUE,
  orderValue = COLUMN_ORDER_VALUE_INDICATOR_LATEST_VALUE,
  displayValue = COLUMN_ID_INDICATOR_LATEST_VALUE_DISPLAY_VALUE,
}: IndicatorLatestValueColumnProps): DatatableColumn<IndicatorListItem> => {
  return {
    columnId: columnId,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => displayValue,
    getOrderValue: () => orderValue, // prefix with 100, 200, 300 to assign to sections
    getColumnDef: () => {
      return {
        id: columnId,
        accessorKey: columnId, // key needed to enable sorting / filtering
        enableHiding: true,
        enableSorting: true,
        enableColumnFilter: true,
        size: 1, // set to 1 instead to 0 for it to work in Safari
        sortingFn: (rowA, rowB) => {
          if (!isRawRow(rowA.original)) {
            return 0;
          }

          if (!isRawRow(rowB.original)) {
            return 0;
          }

          const valueA = rowA.original.latestValue?.value;
          const valueB = rowB.original.latestValue?.value;

          const nullOrUndefined = (value: number | null | undefined): value is null | undefined => {
            return value === null || value === undefined;
          };

          if (nullOrUndefined(valueA) && nullOrUndefined(valueB)) {
            return 0;
          }

          if (nullOrUndefined(valueA)) {
            return 1;
          }

          if (nullOrUndefined(valueB)) {
            return -1;
          }

          return valueA - valueB;
        },
        filterFn: (row, _columnId, value, _addMeta) => {
          if (!isRawRow(row.original)) {
            return true;
          }

          return filterByNumberWithUnknownPayload(value, row.original.latestValue?.value);
        },
        header: ({ column }) => (
          <DatatableColumnHeaderCell column={column} className={`text-xs font-normal`}>
            {displayValue}
          </DatatableColumnHeaderCell>
        ),
        cell: ({ row }) => {
          if (!isRawRow(row.original)) {
            return null;
          }

          const hasLatestValue = row.original.latestValue !== null && row.original.latestValue !== undefined;

          return (
            <DatatableCellNumber row={row}>
              <div className="flex w-full flex-row items-center justify-end gap-2 whitespace-nowrap pl-2 pr-8 text-muted-foreground">
                <span className="whitespace-nowrap">{`${hasLatestValue ? row.original.latestValue?.value : "–"}`}</span>
              </div>
            </DatatableCellNumber>
          );
        },
      };
    },
    renderDisplayView: () => <FilterByNumberDisplayView displayValue={displayValue} />,
    getFilterConfigurationViewType: () => "popover",
    renderFilterConfigurationView: (onFinished: () => void) => (
      <FilterByNumberConfigurationView column={{ id: columnId }} onFinished={onFinished} />
    ),
    renderAppliedFilterView: () => (
      <FilterByNumberAppliedFilterView
        column={{ id: columnId }}
        renderDisplayView={() => <FilterByNumberDisplayView displayValue={displayValue} />}
      />
    ),
  };
};
